// Home screen: main menu + character preview + stats pills + changing-room
// sub-panel. Replaces the old JoinScreen as the pre-game entry point.
//
// Layout is a single wide horizontal stage (2640×1080) that holds both the
// home viewport (menu + character + HUD pills) and the changing-room viewport
// (form fields on the right). Clicking CHANGING ROOM pans the wide stage left
// by 720px so the character appears to slide to the left half of the screen
// while the form is revealed on the right — no page navigation. Clicking DONE
// pans back. The other four menu items resolve show() with the current
// JoinConfig and let main.ts hand off to MapSelectScreen.
//
// The 3D preview, hat-icon rendering, drag-rotate, gamepad polling, look-around
// animation, and dev-mode hat-config panel are migrated verbatim from the old
// JoinScreen; only the DOM scaffolding and styles around them changed.

import * as THREE from 'three';
import { PMREMGenerator, WebGPURenderer } from 'three/webgpu';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { createGLTFLoader } from '../render/glbLoader';
import { CharacterView } from '../render/CharacterView';
import { loadHatModel, type HatConfig, type HatId } from '../render/HatModels';
import { Ragdoll, Bone, BONE_COUNT, quatFromAxisAngle, quatMul } from '../sim/Ragdoll';
import { emptySnapshot, type CharacterSnapshot } from '../sim/SimState';
import { CONFIG } from '../config';
import { badgeColorsFromBase, paletteFromBase } from './palette';
import { menuAudio } from './MenuAudio';
import { MenuNav, type MenuItem } from './MenuNav';
import { StageScaler, STAGE_W, STAGE_H } from './StageScaler';
import { showSettingsModal } from './SettingsScreen';
import { t, onLocaleChange } from '../i18n';
import { startProbing as startRegionProbing } from '../net/regionPing';
import { isDevMode } from './devMode';
import { accountState, type AccountDTO } from '../profile/account';
import { getLastSeenLevel, setLastSeenLevel } from '../profile/accountStore';
import { getLocalWins } from '../profile/winsStore';

/** Selection state: 'none' is a distinct choice; everything else maps to a
 *  real hat model. */
export type HatSelection = 'none' | HatId;

export interface JoinConfig {
  name: string;
  hatId: HatSelection;
  color: string;
  /** Which downstream flow the user picked from the home menu. Defaults to
   *  'play' (LOCAL play: map-select → local match); 'online' enters the
   *  matchmaking → vote → networked match flow; 'editor' hands control to
   *  MapEditorScreen instead. */
  action?: 'play' | 'online' | 'editor' | 'leaderboard';
}

interface HatDef {
  id: HatSelection;
  /** i18n key for the human-readable label. Resolved via t() at render time
   *  so the picker re-localises on language change without a registry rebuild. */
  labelKey: string;
}

/** Config fields the dev panel edits — order used for sliders, readout, and
 *  the emitted JSON so the pasted output matches the visual layout. */
const DEV_KEYS = ['scale', 'offsetX', 'offsetY', 'offsetZ', 'yawDeg', 'pitchDeg', 'rollDeg'] as const;

const HATS: HatDef[] = [
  { id: 'none',        labelKey: 'hats.none' },
  { id: 'top',         labelKey: 'hats.top' },
  { id: 'chef',        labelKey: 'hats.chef' },
  { id: 'baseballCap', labelKey: 'hats.baseballCap' },
  { id: 'beanie',      labelKey: 'hats.beanie' },
  { id: 'bucket',      labelKey: 'hats.bucket' },
  { id: 'cowboy',      labelKey: 'hats.cowboy' },
  { id: 'firefighter', labelKey: 'hats.firefighter' },
  { id: 'crown',       labelKey: 'hats.crown' },
  { id: 'graduation',  labelKey: 'hats.graduation' },
  { id: 'halo',        labelKey: 'hats.halo' },
  { id: 'party',       labelKey: 'hats.party' },
  { id: 'pirate',      labelKey: 'hats.pirate' },
  { id: 'santa',       labelKey: 'hats.santa' },
  { id: 'sombrero',    labelKey: 'hats.sombrero' },
  { id: 'viking',      labelKey: 'hats.viking' },
  { id: 'wizard',      labelKey: 'hats.wizard' }
];

// Six designer-approved hues + a custom slot. Hex values picked to match the
// saturation/brightness of the mock-up (not pure primaries — those clash with
// the eggshell blue background).
export const PRESET_COLORS: string[] = [
  '#ffae00', // hot orange
  '#00b3ff', // blue
  '#0bea1a', // green
  '#F0CC1A', // yellow
  '#E61FB2', // magenta
  '#962eff'  // purple
];

const NAME_POOL: string[] = [
  'Bouncy', 'Wobbly', 'Flop', 'Noodle', 'Rubber', 'Jelly', 'Squish', 'Tumble',
  'Spring', 'Boing', 'Floppy', 'Wiggle', 'Bonky', 'Thwack', 'Slappy', 'Whomp',
  'Stumbly', 'Dizzy', 'Flail', 'Kick'
];
const NAME_SUFFIX: string[] = [
  'Pants', 'Boots', 'Fingers', 'Knees', 'Elbows', 'Chin', 'Brawler', 'Smasher',
  'McFlop', 'Jr', 'the Great', 'the Mighty', 'Supreme', 'Prime', 'X'
];

export function randomName(): string {
  const a = NAME_POOL[Math.floor(Math.random() * NAME_POOL.length)]!;
  const b = NAME_SUFFIX[Math.floor(Math.random() * NAME_SUFFIX.length)]!;
  return `${a} ${b}`;
}

// ---------------------------------------------------------------------------
// Profile persistence — the player's picks (name / hat / color) survive a
// reload. Stored as one JSON blob so we read/write a single localStorage key
// and the three fields stay in sync.

const STORAGE_KEY = 'games:homeProfile';
const HAT_IDS: ReadonlySet<HatSelection> = new Set(HATS.map((h) => h.id));
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface StoredProfile {
  name?: string;
  hatId?: HatSelection;
  color?: string;
}

function loadProfile(): StoredProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const p = parsed as Record<string, unknown>;
    const out: StoredProfile = {};
    if (typeof p.name === 'string') out.name = p.name.slice(0, 20);
    if (typeof p.hatId === 'string' && HAT_IDS.has(p.hatId as HatSelection)) out.hatId = p.hatId as HatSelection;
    if (typeof p.color === 'string' && HEX_RE.test(p.color)) out.color = p.color;
    return out;
  } catch {
    return {};
  }
}

function saveProfile(p: StoredProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* quota / disabled — silently skip */
  }
}

// Stats displayed on the bottom-left of the home screen, derived from the
// soft-auth account (online wins / xp / level) plus the local-only wins
// counter. The account is loaded by main.ts before the home mounts; if the
// fetch failed the home falls back to a sensible "level 1, 0 XP" placeholder.
interface HomeStats {
  level: number;
  titleKey: string;
  xpInto: number;
  xpToNext: number;
  totalWins: number;
  maxLevel: boolean;
  online: boolean;
}

function computeHomeStats(): HomeStats {
  const account = accountState.get();
  const local = getLocalWins();
  if (!account) {
    return { level: 1, titleKey: 'home.brawlingNoob', xpInto: 0, xpToNext: 1, totalWins: local, maxLevel: false, online: false };
  }
  return {
    level: account.level,
    titleKey: 'home.brawlingNoob',
    xpInto: account.xp_into_level,
    xpToNext: account.xp_to_next,
    totalWins: account.online_wins + local,
    maxLevel: account.level >= account.max_level,
    online: true,
  };
}

// Menu entries, in display order. `changing` is the only id that triggers the
// pan; every other id resolves show() and hands off to MapSelectScreen.
type MenuItemId = 'online' | 'local' | 'tutorial' | 'mapEditor' | 'changing' | 'leaderboard' | 'settings';
const IS_LOCAL_HOST = typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
// Touch-primary devices (phones, most tablets) can't drive the editor's gizmo
// + tree + inspector layout. Hide the entry on those devices rather than
// shipping a broken affordance.
const IS_TOUCH_PRIMARY = typeof window !== 'undefined'
  && (matchMedia('(pointer: coarse)').matches
    || ((navigator.maxTouchPoints ?? 0) > 0 && !matchMedia('(hover: hover)').matches));
// Online play is hidden by default — set VITE_ONLINE_ENABLED=1 in the env
// to expose the entry. Off in any build that hasn't opted in.
const ONLINE_ENABLED = ((import.meta.env as unknown as { VITE_ONLINE_ENABLED?: string })
  .VITE_ONLINE_ENABLED) === '1';
const MENU_ITEMS: { id: MenuItemId; labelKey: string }[] = [
  ...(ONLINE_ENABLED ? [{ id: 'online' as MenuItemId, labelKey: 'menu.online' }] : []),
  { id: 'local',     labelKey: 'menu.local' },
  // { id: 'tutorial',  labelKey: 'menu.tutorial' },
  ...(IS_LOCAL_HOST && !IS_TOUCH_PRIMARY ? [{ id: 'mapEditor' as MenuItemId, labelKey: 'menu.editor' }] : []),
  { id: 'changing',  labelKey: 'menu.changingRoom' },
  { id: 'leaderboard', labelKey: 'menu.leaderboard' },
  { id: 'settings',  labelKey: 'menu.settings' }
];

// ---------------------------------------------------------------------------
// Styles

// Wide stage holds both the home viewport (0–1920) and the changing-room
// viewport (720–2640). Panning translates by WIDE_PAN so the right column
// slides into view. STAGE_W / STAGE_H come from StageScaler.
const WIDE_STAGE_W = 2640;
const WIDE_PAN = 720;
const FLOOR_BAR_H = 90;

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#home-screen { position: fixed; inset: 0; z-index: 100; background: linear-gradient(180deg, #4DB1CF 0%, #7DBACE 59.62%, #75C3DC 66.35%, #4DB1CF 75.96%, #4DB1CF 100%); font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif; color: #0E3C4A; overflow: hidden; clip-path: inset(0 0 0 0); transition: clip-path 700ms cubic-bezier(0.65, 0, 0.35, 1); }
#home-screen.hiding { clip-path: inset(0 100% 0 0); pointer-events: none; }

/* Viewport-level blue floor. Lives BEHIND the scaled stage (z-index 0),
   spans full viewport width, height = scaled in-stage bar + any vertical
   letterbox gap below the stage. That way the blue always reaches all
   three viewport edges regardless of aspect ratio. */
#home-screen .floor-fill { position: absolute; left: 0; right: 0; bottom: 0; height: var(--floor-h, 90px); background: #7AC3D9; z-index: 0; transition: opacity 220ms ease; pointer-events: none; }
#home-screen.changing .floor-fill { opacity: 0; transition-delay: 60ms; }

/* Viewport-level HUD. Sits IN FRONT of the scaled stage (z-index 2) so the
   LVL card + pills visually overlay the character's feet/shadow rather
   than being occluded by them. Inside the hud, children anchor to the
   viewport corners (not the scaled stage) and carry their own transform
   scale so they shrink proportionally with the rest of the UI. */
#home-screen .viewport-hud { position: absolute; left: 0; right: 0; bottom: 0; height: var(--floor-h, 90px); z-index: 2; pointer-events: none; transition: opacity 220ms ease; }
#home-screen.changing .viewport-hud { opacity: 0; transition-delay: 60ms; }

/* Fixed-size stage, scaled uniformly to the viewport at runtime. */
#home-screen .stage { position: absolute; top: 50%; left: 50%; width: ${STAGE_W}px; height: ${STAGE_H}px; transform-origin: center center; transform: translate(-50%, -50%) scale(1); }

/* Wide inner stage — 2640px wide. Translating this element horizontally is
   the camera pan that reveals the changing-room panel. */
#home-screen .wide-stage { position: absolute; left: 0; top: 0; width: ${WIDE_STAGE_W}px; height: ${STAGE_H}px; transform: translateX(0); transition: transform 560ms cubic-bezier(0.65, 0, 0.35, 1); }
#home-screen.changing .wide-stage { transform: translateX(-${WIDE_PAN}px); }

/* ---- Character preview (shared between both views) ----
   Full stage height: the canvas owns the whole vertical column so the soft
   shadow has room to drape in front of the character without being clipped
   at the canvas's bottom edge.

   Solo: 520×1080 at left=1040 (canvas centered around stage x=1300, which
   keeps the local player visually anchored where they were on the original
   home).
   Party (party-active class): widens to 1300×1080 at left=650 — the same
   stage x=1300 center, so the local character does NOT shift when the
   canvas grows. Additional party-mate characters are drawn around them
   by MultiCharacterStage. */
#home-screen .preview {
  position: absolute;
  left: 1040px; top: 0; width: 520px; height: 1080px;
  transition: left 360ms cubic-bezier(0.65, 0, 0.35, 1), width 360ms cubic-bezier(0.65, 0, 0.35, 1);
}
#home-screen.party-active .preview {
  left: 650px; width: 1300px;
}
#home-screen .preview canvas { width: 100%; height: 100%; display: block; background: transparent; cursor: grab; touch-action: none; user-select: none; }
#home-screen .preview canvas.dragging { cursor: grabbing; }

/* "YOU" head label — HTML overlay. Rendered at 1.5× the base viewBox
   (108×93) and positioned so the tip lands ~10px above the hat top. The
   camera has been pushed up (lookAt y=1.8) so the hat renders around
   canvas y=153, leaving clear sky above for the badge to sit close. */
#home-screen .head-label { position: absolute; left: 1040px; top: 100px; width: 520px; height: 93px; display: flex; justify-content: center; align-items: flex-end; pointer-events: none; }
#home-screen .head-label svg { width: 108px; height: 93px; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.28)); }

/* ---- Home viewport content (menu, LVL card, bottom pills) ---- */
#home-screen .menu-list { position: absolute; left: 120px; top: 160px; display: flex; flex-direction: column; align-items: flex-start; gap: 20px; transition: opacity 220ms ease, transform 220ms ease; }
#home-screen.changing .menu-list { opacity: 0; pointer-events: none; transform: translateX(-60px); transition-delay: 60ms, 60ms; }
/* Online-play mode: same slide-out as changing room, but a different class
   so the two never fight each other. Online play replaces the menu with a
   party panel (mounted by OnlineFlowController) — the home itself doesn't
   own that panel; we just clear the menu's slot. */
#home-screen.online-active .menu-list { opacity: 0; pointer-events: none; transform: translateX(-60px); transition-delay: 60ms, 60ms; }
/* Hide the YOU label while in online-active mode — MultiCharacterStage
   manages party-member name pins that cover this same screen area. */
#home-screen.online-active .head-label { opacity: 0; transition: opacity 200ms ease; }

/* Pale sea-foam text with a light-blue top-left shadow for the chunky 3D
   feel; a gentle 1.57° tilt keeps the list playful. Padding + rotation
   are CONSTANT across idle/hover — the white pill is painted as an
   ::before pseudo-element behind the text so highlighting an item never
   reflows the list. Only colour, text-shadow, and the pill's opacity
   change when an item becomes active. */
#home-screen .menu-item { position: relative; isolation: isolate; border: 0; cursor: pointer; font-family: 'Chewy', sans-serif; font-size: 82px; line-height: 1; color: #EEF4F6; text-shadow: -4px -1px 0px #B4DCE8; transform: rotate(1.57deg); letter-spacing: 0.02em; background: transparent; padding: 18px 48px; border-radius: 56px; transition: color 200ms ease, text-shadow 200ms ease; text-align: left; white-space: nowrap; }
#home-screen .menu-item::before { content: ""; position: absolute; inset: 0; background: #FFFFFF; border-radius: 56px; box-shadow: -6px 0 0 #B9D6DF, 0 10px 22px rgba(0,0,0,0.18); opacity: 0; z-index: -1; transition: opacity 200ms ease; pointer-events: none; }
#home-screen .menu-item:focus { outline: none; }
/* .focused is painted by MenuNav whenever a menu item owns focus — via
   keyboard arrows, gamepad, or mouse hover (MenuNav syncs mouseenter into
   focus). Styled identically for all three input paths so the user always
   sees the same pill highlight regardless of how they navigated. */
#home-screen .menu-item:hover,
#home-screen .menu-item.focused { color: #0E3C4A; text-shadow: none; }
#home-screen .menu-item:hover::before,
#home-screen .menu-item.focused::before { opacity: 1; }

/* LVL card: sits inside the viewport-level .viewport-hud, anchored to its
   bottom-left (= viewport bottom-left). Transform scale keeps it
   proportional to the rest of the UI. Only the top-right corner is
   rounded so the bottom + left edges meet the viewport cleanly. XP bar +
   text are absolutely positioned so the XP text clings to the bar rather
   than stacking evenly with LVL/rank. */
#home-screen .lvl-card { position: absolute; left: 0; bottom: 0; width: 320px; height: 160px; background: #E3EDF0; box-shadow: 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; border-radius: 0 45px 0 0; padding: 24px 32px 0 32px; box-sizing: border-box; display: flex; flex-direction: column; justify-content: flex-start; gap: 4px; transform-origin: bottom left; transform: scale(var(--stage-scale, 1)); pointer-events: auto; }
#home-screen .lvl-card .lvl { font-family: 'Chewy', sans-serif; font-size: 36px; line-height: 1; color: #0E3C4A; letter-spacing: 0.04em; }
#home-screen .lvl-card .title { font-family: 'Chewy', sans-serif; font-size: 24px; line-height: 0.8; color: #1FA4E0; }
/* XP text pulled down near the bar (rather than stacking evenly with LVL
   and Brawling Noob at the top) so the gap above it reads as a "break"
   between the identity block and the progress block. */
#home-screen .lvl-card .xp-text { position: absolute; left: 32px; bottom: 42px; font-family: 'Chewy', sans-serif; font-size: 16px; line-height: 1; color: #FF7F1F; letter-spacing: 0.06em; }
#home-screen .lvl-card .xp-track { position: absolute; left: 32px; right: 32px; bottom: 26px; height: 8px; background: rgba(14,60,74,0.12); border-radius: 4px; }
#home-screen .lvl-card .xp-fill { height: 100%; background: linear-gradient(90deg, #FFC864 0%, #FF8A1E 100%); border-radius: 4px; box-shadow: 0 0 10px rgba(255,140,40,0.9), 0 0 4px rgba(255,200,90,1), inset 0 1px 0 rgba(255,255,255,0.6); transition: width 600ms cubic-bezier(0.2, 0.9, 0.2, 1); }
@keyframes home-lvl-pulse {
  0%   { transform: scale(var(--stage-scale, 1)); box-shadow: 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; }
  20%  { transform: scale(calc(var(--stage-scale, 1) * 1.06)); box-shadow: 0 0 28px rgba(255, 200, 70, 0.95), 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; }
  40%  { transform: scale(calc(var(--stage-scale, 1) * 1.03)); box-shadow: 0 0 22px rgba(255, 180, 60, 0.85), 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; }
  100% { transform: scale(var(--stage-scale, 1)); box-shadow: 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; }
}
#home-screen .lvl-card.level-up-pulse { animation: home-lvl-pulse 1.6s cubic-bezier(0.2, 0.9, 0.2, 1) both; }

/* Bottom pills — viewport-centered. Bottom offset and transform scale are
   both driven by --stage-scale so the pills hug the floor consistently
   at every viewport size. */
#home-screen .bottom-pills { position: absolute; left: 50%; bottom: calc(var(--stage-scale, 1) * 15px); transform: translateX(-50%) scale(var(--stage-scale, 1)); transform-origin: bottom center; display: flex; gap: 20px; pointer-events: auto; }
#home-screen .pill { background: #E3EDF0; box-shadow: 0px 3px 4px #59A8C0, inset 0px 3px 10px #FFFFFF, inset 0px -3px 4px #AFD6E2; border-radius: 18px; padding: 8px 22px; display: flex; align-items: center; gap: 12px; height: 60px; box-sizing: border-box; font-family: 'Chewy', sans-serif; font-size: 28px; line-height: 1; letter-spacing: 0.02em; }
#home-screen .pill img { width: 38px; height: 38px; object-fit: contain; display: block; }
#home-screen .pill.wins { color: #FF7F1F; }
#home-screen .pill.rank { color: #1FA4E0; }

/* ---- Changing-room viewport (form on the right) ----
   Positioned at wide-stage x=1620 (gap of only ~60px from the character's
   right edge after the pan), compact content using a 6-column hat grid
   so the whole form is shorter and the character + form sit close
   together rather than stretched across the viewport. */
#home-screen .form-panel { position: absolute; left: 1620px; top: 130px; width: 680px; height: 820px; display: flex; flex-direction: column; gap: 40px; padding: 16px 24px; box-sizing: border-box; opacity: 0; visibility: hidden; transition: opacity 280ms ease, visibility 0s linear 280ms; }
#home-screen.changing .form-panel { opacity: 1; gap: 40px; visibility: visible; transition: opacity 320ms ease 220ms, visibility 0s linear 220ms; }

/* 44px Chewy label with a chunky white stroke. */
#home-screen .field-label { font-family: 'Chewy', sans-serif; font-size: 44px; line-height: 1; color: #0E3C4A; -webkit-text-stroke: 5px #FFFFFF; paint-order: stroke fill; margin: 0 0 10px 0; }

/* Hat grid — 6 columns × 3 rows (18 slots, 17 filled). 92px buttons with
   10px gap: 6*92+5*10=602 wide, 3*92+2*10=296 tall. Fits the 632px
   content width with ~300px of vertical budget, keeping the whole form
   short enough to sit between name and color without scrolling. */
#home-screen .hat-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; max-width: 632px; }
#home-screen .hat-opt { position: relative; width: 92px; height: 92px; border-radius: 16px; border: 0; padding: 4px; box-sizing: border-box; cursor: pointer; background: #FFFFFF; box-shadow: 0 1px 4px rgba(0,0,0,0.22); transition: background 120ms ease; }
#home-screen .hat-opt .inner { width: 100%; height: 100%; border-radius: 12px; background: #FFFFFF; display: flex; align-items: center; justify-content: center; overflow: hidden; position: relative; }
#home-screen .hat-opt .inner .label { font-family: 'Chewy', sans-serif; font-size: 18px; color: #0E3C4A; }
#home-screen .hat-opt .inner canvas, #home-screen .hat-opt .inner img.hat-icon { width: 100%; height: 100%; object-fit: contain; display: block; }
#home-screen .hat-opt.selected { background: linear-gradient(180deg, #73C5DD 0%, #17A4CD 100%); box-shadow: 0 1px 4px rgba(0,0,0,0.22); }
#home-screen .hat-opt.selected .inner { background: #FFFFFF; box-shadow: 0 2px 4px rgba(0,0,0,0.25), inset 0 -3px 3px #DBEEF3; }
#home-screen .hat-opt .check { position: absolute; top: -7px; right: -7px; width: 28px; height: 28px; pointer-events: none; display: none; filter: drop-shadow(0 3px 8px rgba(0,0,0,0.25)); }
#home-screen .hat-opt.selected .check { display: block; }

/* Color grid */
#home-screen .color-row { display: flex; gap: 14px; align-items: center; }
#home-screen .color-opt { position: relative; width: 52px; height: 52px; border-radius: 10px; border: 0; padding: 4px; box-sizing: border-box; cursor: pointer; background: #FFFFFF; box-shadow: 0 1px 4px rgba(0,0,0,0.22); transition: background 120ms ease; }
#home-screen .color-opt .swatch { width: 100%; height: 100%; border-radius: 8px; background: #FFFFFF; display: flex; align-items: center; justify-content: center; }
#home-screen .color-opt .swatch .dot { width: 24px; height: 24px; border-radius: 50%; background: var(--c, #E42A1E); }
#home-screen .color-opt.selected { background: linear-gradient(180deg, #73C5DD 0%, #17A4CD 100%); box-shadow: 0 1px 4px rgba(0,0,0,0.22); }
#home-screen .color-opt.selected .swatch { background: #FFFFFF; box-shadow: inset 0 -2px 2px #DBEEF3; }
#home-screen .color-opt .check { position: absolute; top: -6px; right: -6px; width: 22px; height: 22px; pointer-events: none; display: none; filter: drop-shadow(0 3px 6px rgba(0,0,0,0.22)); }
#home-screen .color-opt.selected .check { display: block; }
#home-screen .color-opt.custom { width: auto; padding: 4px; height: 52px; display: flex; align-items: center; }
#home-screen .color-opt.custom .swatch { width: 100%; height: 100%; padding: 0 12px 0 10px; gap: 8px; justify-content: flex-start; }
#home-screen .color-opt.custom .swatch .dot { width: 20px; height: 20px; background: #D8D8D8; flex: 0 0 auto; }
#home-screen .color-opt.custom .swatch .txt { font-family: 'Chewy', sans-serif; font-size: 22px; color: #0E3C4A; }
#home-screen .color-opt.custom input[type="color"] { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }

/* Done button */
#home-screen .done-row { display: flex; margin-top: 4px; }
#home-screen .done-btn { width: 240px; height: 80px; background: linear-gradient(180deg, #D2EFF9 0%, #B7EAFA 6.73%, #B7EAFA 93.75%, #94DDF4 100%); box-shadow: 6px 0 0 #A5D6E5, 4px 2px 10px #83A8B3; border-radius: 20px; border: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; font-family: 'Chewy', sans-serif; font-size: 40px; color: #0D7393; letter-spacing: 0.06em; transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease; }
#home-screen .done-btn:active { transform: translate(3px, 2px); box-shadow: 3px 0 0 #A5D6E5, 1px 0 6px #83A8B3; }

/* Focus rings for every nav-targetable field in the changing room. Native
   :hover lift is added alongside .focused so mouse users see the same
   affordance. Ring is a thin cyan outline offset outside each button so
   it reads on both the white hat/color cards and the blue DONE button
   without fighting their existing drop shadows. */
#home-screen .hat-opt:hover,
#home-screen .color-opt:hover,
#home-screen .done-btn:hover { filter: brightness(1.04); }
#home-screen .hat-opt.focused,
#home-screen .color-opt.focused,
#home-screen .done-btn.focused { outline: 4px solid rgba(255, 255, 255, 0.5); outline-offset: 3px; }
#home-screen .hat-opt.focused,
#home-screen .color-opt.focused { filter: brightness(1.04); }

/* Dev mode — viewport-fixed so it keeps constant screen size regardless of
   stage scale. Only visible when 'Dev Mode' is toggled on. */
#home-screen .dev-toggle { position: fixed; right: 16px; bottom: 16px; z-index: 2; background: #1e2530; color: #cfe3ef; border: 1px solid #38485a; padding: 10px 14px; border-radius: 8px; font: 600 12px ui-sans-serif, system-ui, sans-serif; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; opacity: 0.75; transition: opacity 140ms ease; }
#home-screen .dev-toggle:hover { opacity: 1; }
#home-screen .dev-panel { position: fixed; right: 16px; bottom: 64px; width: 340px; z-index: 2; background: #1e2530; color: #e3ecf4; border: 1px solid #38485a; border-radius: 10px; padding: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.35); font: 12px ui-sans-serif, system-ui, sans-serif; display: none; }
#home-screen.dev-on .dev-panel { display: block; }
#home-screen .dev-panel h3 { margin: 0 0 10px; font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase; color: #9fc1d8; display: flex; justify-content: space-between; align-items: center; }
#home-screen .dev-panel h3 button { background: #2a3543; color: #cfe3ef; border: 1px solid #4a5a6e; padding: 4px 10px; border-radius: 5px; font: 600 11px ui-sans-serif, sans-serif; cursor: pointer; letter-spacing: 0.06em; }
#home-screen .dev-row { display: grid; grid-template-columns: 72px 1fr 52px; align-items: center; gap: 8px; margin: 6px 0; }
#home-screen .dev-row label { color: #8ea8bb; font-size: 11px; }
#home-screen .dev-row input[type="range"] { width: 100%; }
#home-screen .dev-row .val { font: 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #cfe3ef; text-align: right; }
#home-screen .dev-upload { margin-top: 12px; padding-top: 12px; border-top: 1px solid #38485a; }
#home-screen .dev-upload label { display: block; color: #8ea8bb; font-size: 11px; margin-bottom: 6px; }
#home-screen .dev-upload input[type="file"] { font-size: 11px; color: #cfe3ef; width: 100%; }
#home-screen .dev-readout { margin-top: 12px; padding: 10px; background: #141a22; border-radius: 6px; font: 11px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; color: #bfe0f2; white-space: pre; overflow-x: auto; }
`;

// ---------------------------------------------------------------------------
// "YOU" head label template. Exported so the future in-game nameplate system
// can reuse the exact same shape — the in-world version will typically pass a
// wider geometry + a different baseColor (per-player) and may swap the large
// triangle tip for a smaller chevron.

export interface HeadLabelOptions {
  text: string;
  /** Body base fill (gradient is derived by lightening/darkening). */
  baseColor?: string;
  /** Outline color around the body + text. */
  strokeColor?: string;
  /** Gradient top highlight color. */
  highlightColor?: string;
}

/** Returns an inline <svg> element styled as a downward-pointing pin with
 *  centered text. Template used by both the home-screen "YOU" label and the
 *  future in-game player nameplates (which will extend with wider body +
 *  chevron tail).
 *
 *  The pin is tight around the text (no excess padding) and the rectangular
 *  body flows into the triangle tip via quadratic curves, so the left/right
 *  sides of the body round into the triangle rather than meeting it at a
 *  sharp corner. */
export function renderHeadLabelSvg(opts: HeadLabelOptions): string {
  const baseColor = opts.baseColor ?? '#E5571F';
  const strokeColor = opts.strokeColor ?? '#7A2A0E';
  const highlightColor = opts.highlightColor ?? '#FF8A4C';
  const gradId = `hl-grad-${Math.random().toString(36).slice(2, 8)}`;
  // viewBox tuned for the "YOU" text at 30px Chewy: body 72×44, triangle tip
  // 18px tall with curved shoulders (via Q curves) instead of a sharp V.
  return `
    <svg viewBox="0 0 72 62" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="${highlightColor}"/>
          <stop offset="55%" stop-color="${baseColor}"/>
          <stop offset="100%" stop-color="${darken(baseColor, 0.25)}"/>
        </linearGradient>
      </defs>
      <path d="M12 2 H60 A10 10 0 0 1 70 12 V36 Q70 44 62 46 L40 56 Q36 58 32 56 L10 46 Q2 44 2 36 V12 A10 10 0 0 1 12 2 Z"
            fill="url(#${gradId})" stroke="${strokeColor}" stroke-width="3" stroke-linejoin="round"/>
      <text x="36" y="24" fill="#FFFFFF" stroke="${strokeColor}" stroke-width="3"
            text-anchor="middle" dominant-baseline="central"
            font-family="Chewy, sans-serif" font-size="22"
            style="paint-order: stroke fill;">${escapeSvgText(opts.text)}</text>
    </svg>
  `;
}

function escapeSvgText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Darken a hex color by multiplying each channel. Used to derive the bottom
 *  gradient stop for head labels from a base color. */
function darken(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const f = 1 - amount;
  const toHex = (n: number): string => Math.max(0, Math.min(255, Math.round(n * f))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ---------------------------------------------------------------------------
// Hat icon renderer — builds a transparent mini scene per hat, auto-frames
// the model, renders once. The WebGL context is retained so AA survives
// (toDataURL() on transparent WebGL canvases loses AA on some drivers).

async function renderHatIcon(id: HatId, size: number): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const renderer = new WebGPURenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  await renderer.init();
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(size, size, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const pmrem = new PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();
  scene.add(new THREE.AmbientLight(0xffffff, 0.85));
  const dir = new THREE.DirectionalLight(0xfff2d8, 1.9);
  dir.position.set(-1.5, 2.5, 2);
  scene.add(dir);

  const hat = (await loadHatModel(id)).clone(true);
  // Template comes pre-neutralised from the loader's cache; the cloned
  // instance shares those materials by reference.

  hat.rotation.y = CONFIG.hats[id].yawDeg * Math.PI / 180;
  scene.add(hat);

  const box = new THREE.Box3().setFromObject(hat);
  const center = new THREE.Vector3(); box.getCenter(center);
  const bsize = new THREE.Vector3(); box.getSize(bsize);
  hat.position.sub(center);
  const maxDim = Math.max(bsize.x, bsize.y, bsize.z);
  const fovRad = 24 * Math.PI / 180;
  const dist = (maxDim * 0.5) / Math.tan(fovRad * 0.5) * 1.22;
  const camera = new THREE.PerspectiveCamera(24, 1, 0.01, 100);
  camera.position.set(0, 0, dist);
  camera.lookAt(0, 0, 0);

  renderer.render(scene, camera);

  (canvas as unknown as { _owner: { renderer: WebGPURenderer } })._owner = { renderer };
  return canvas;
}

// ---------------------------------------------------------------------------

export class HomeScreen {
  /** Exposed so a host container (e.g. the splash screen's scroll column)
   *  can reparent this element into itself — keeps the home as part of
   *  the same "tall illusion" scroll rather than a separate z-layer. */
  readonly root: HTMLElement;
  private styleEl: HTMLStyleElement;
  private doneBtn!: HTMLButtonElement;

  // Preview scene
  private canvas!: HTMLCanvasElement;
  private renderer!: WebGPURenderer;
  private setupPromise!: Promise<void>;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private view!: CharacterView;
  private dirLight!: THREE.DirectionalLight;

  // Synthetic bone snapshots for the static pose.
  private prevSnap: CharacterSnapshot = emptySnapshot(0, BONE_COUNT);
  private currSnap: CharacterSnapshot = emptySnapshot(0, BONE_COUNT);

  private rafHandle = 0;
  private lastFrameMs = 0;
  private disposed = false;
  private iconCanvases: HTMLCanvasElement[] = [];

  // State
  private selectedHat: HatSelection = 'top';
  private selectedColor: string = PRESET_COLORS[0]!;
  private inChangingRoom = false;

  private basePositions!: readonly [number, number, number][];
  private userYaw = 0;

  // Dev mode
  private devMode = false;
  private devReadout!: HTMLElement;
  private hatConfigSnapshot!: Record<HatId, HatConfig>;

  // Drag state
  private dragging = false;
  private dragPointerId = -1;

  // Mouse-follow head state (world-frame yaw + pitch, smoothed toward target).
  // Target angles are computed from mouse position relative to the head's
  // on-screen centre each frame; current angles lerp toward target. Replaces
  // the old periodic left→forward→right state machine.
  private mouseX = innerWidth / 2;
  private mouseY = innerHeight / 2;
  private headLookYaw = 0;
  private headLookPitch = 0;

  // 87 BPM head nod — one nod per beat of music-home.wav. The nod is an
  // additional pitch applied relative to the head's mouse-look
  // orientation, so the character appears to nod "down and up" along
  // its current gaze line rather than rotating up and down relative to
  // world vertical. Implemented as an angle added to headLookPitch
  // before building qPitchHead: both rotations live on the world-+X
  // axis in the rest frame (which becomes the head's ear-axis after
  // yaw), and rotations around the same axis commute + add — so the
  // nod automatically rides the head's current look direction without
  // any extra quaternion composition.
  //
  // Envelope is asymmetric: ~35% of the beat is the down stroke
  // (noticeably brisker, reads as a small "stop" at the bottom since
  // the same sine ease is compressed into less time), the remaining
  // ~65% is a long smooth lift back to centre. Peak amplitude is small
  // (~4.3°) — visibly alive but subtler than the old Y-translation bob.
  private static readonly NOD_BPM = 87;
  private static readonly NOD_AMPLITUDE = 0.075;
  private static readonly NOD_DOWN_FRAC = 0.35;
  /** Tweak-knob (seconds) for lining the bottom-of-nod up with the music's
   *  downbeat. Positive delays the nod (peak lands later relative to
   *  wall-clock); negative advances it. Tune by ear — any value works,
   *  the phase wraps so it's safe to go outside [0, 1 beat). */
  private static readonly NOD_START_DELAY_SEC = 0.5;

  // Pause the nod while the player is moving their mouse, resume after a
  // brief debounce of stillness. Phase keeps advancing in real time
  // (computeHeadNodPitch reads performance.now()) so when the nod fades
  // back in it's still on-beat with the music — we only scale the
  // amplitude, not the timing. `nodScale` eases toward the target so the
  // pause/resume are smooth rather than hard snaps.
  private static readonly NOD_IDLE_MS = 500;
  private static readonly NOD_FADE_RATE = 6;
  private lastMouseMoveMs = 0;
  private nodScale = 1;

  /** Active nav manager — main menu, or changing-room grid while the pan is
   *  in the changing-room state. Exactly one is mounted at a time; swapped
   *  on enter/leave changing room. Disposed on screen teardown. */
  private activeNav: MenuNav | null = null;
  /** Stored reference to the main-menu nav so activateNav() can mount it
   *  without rebuilding. Rebuilt each time we leave the changing room to
   *  re-bind the freshly queried DOM elements. */
  private mainMenuNav: MenuNav | null = null;
  /** Tracks whether the first activateNav() has happened — subsequent nav
   *  swaps (leave changing room → main menu) auto-mount since the home is
   *  already interactive by then. */
  private navActivated = false;

  private resolveShow: ((cfg: JoinConfig) => void) | null = null;
  /** External per-frame callbacks. MultiCharacterStage / OnlineFlowController
   *  register here so their pose updates run inside the home's existing
   *  rAF loop rather than spinning a second one. */
  private frameCallbacks: Array<(nowMs: number, dt: number) => void> = [];

  /** Shared 1920×1080 stage scaler. Writes the .stage transform AND the
   *  --stage-scale / --floor-h CSS vars on the home root for the
   *  viewport-anchored HUD pieces. */
  private scaler: StageScaler | null = null;

  /** Detach for the i18n locale-change listener. Called from dispose so
   *  a re-mounted home doesn't receive callbacks for the old instance. */
  private detachLocaleListener: (() => void) | null = null;

  constructor() {
    this.basePositions = Ragdoll.computeSpawnPositions(0, 0, 0);

    this.hatConfigSnapshot = {} as Record<HatId, HatConfig>;
    for (const id of Object.keys(CONFIG.hats) as HatId[]) {
      this.hatConfigSnapshot[id] = { ...CONFIG.hats[id] };
    }

    // Restore previously-saved profile before renderDom runs so the initial
    // markup reflects the stored picks (selected hat button, selected color
    // swatch, filled-in name) rather than painting defaults first and
    // reconciling after.
    const stored = loadProfile();
    if (stored.hatId) this.selectedHat = stored.hatId;
    if (stored.color) this.selectedColor = stored.color;

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.id = 'home-screen';
    this.root.innerHTML = this.renderDom();
    document.body.appendChild(this.root);

    this.wireInputs();
    // Construct the main-menu nav but DON'T start its input listeners yet.
    // On the first-time flow the home is mounted inside the splash, and we
    // need splash's "press any key" keydown handler to win over the menu
    // nav's capture-phase listener for Arrow/Enter. activateNav() — called
    // by main.ts once the splash has dismissed, or by showHomeStandalone
    // immediately after construction — is what actually wires the
    // keyboard/gamepad listeners.
    this.mainMenuNav = this.buildMainMenuNav();
    this.activeNav = this.mainMenuNav;
    this.setupPromise = this.setupPreview();
    this.wireDragRotation();
    this.wireDevMode();
    this.applyColor(this.selectedColor);
    // Start probing region pings in the background so the Online Play
    // region picker is pre-sorted by ping with the lowest selected by
    // default. Skipped when online play is disabled.
    if (ONLINE_ENABLED) startRegionProbing();

    const stageEl = this.root.querySelector('.stage') as HTMLElement;
    this.scaler = new StageScaler({
      stageEl,
      varEl: this.root,
      floorBarDesignH: FLOOR_BAR_H
    });
    this.scaler.start();

    // Re-render translatable text whenever the user picks a new
    // language from settings — so the home screen behind the modal
    // doesn't stay frozen in the old locale.
    this.detachLocaleListener = onLocaleChange(() => this.refreshLocale());

    addEventListener('mousemove', this.onMouseMove);
    addEventListener('pointermove', this.onMouseMove);

    void this.populateHatIcons();
  }

  private onMouseMove = (e: MouseEvent | PointerEvent): void => {
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    this.lastMouseMoveMs = performance.now();
  };

  async show(): Promise<JoinConfig> {
    await this.setupPromise;
    // The rAF loop is self-perpetuating; only start it on the first
    // show() so re-entrant calls (after the user returns from online
    // play without disposing this home instance) don't fork duplicate
    // animation frames.
    if (this.rafHandle === 0) this.loop();
    return new Promise<JoinConfig>((resolve) => {
      this.resolveShow = resolve;
    });
  }

  private resolveAndDismiss(action: 'play' | 'online' | 'editor' | 'leaderboard' = 'play'): void {
    if (!this.resolveShow) return;
    const cfg: JoinConfig = {
      name: accountState.get()?.name ?? randomName(),
      hatId: this.selectedHat,
      color: this.selectedColor,
      action
    };
    // Start the 700ms clip-path swipe immediately and RESOLVE THE PROMISE
    // NOW, not after the swipe completes. Callers (main.ts) chain their
    // next screen off this resolve — if we made them wait for the swipe to
    // finish, the next screen (MapSelectScreen) wouldn't mount until the
    // home was already fully invisible, so the swipe would reveal nothing
    // behind it and the transition sweep SFX would fire out of sync. By
    // resolving early, main synchronously mounts MapSelect at z-index 99
    // (below home's 100) before the swipe paints even its first frame,
    // and the sweep audio lands on the moment the home starts wiping.
    this.root.classList.add('hiding');
    const resolve = this.resolveShow;
    this.resolveShow = null;
    // Dispose the home DOM + renderer after the swipe completes. Stale
    // pointer events are already gated off by `#home-screen.hiding { pointer-events: none }`.
    setTimeout(() => this.dispose(), 700);
    resolve(cfg);
  }

  /** Resolve show() with action='online' WITHOUT dismissing — instead the
   *  home enters online-active mode (menu slides out) and stays mounted so
   *  the OnlineFlowController can render its party panel + party-mate
   *  characters on top of the still-live home. Caller is expected to wire
   *  the home reference to the OnlineFlowController; when the user exits
   *  online play, exitOnlineMode() restores the menu. */
  private resolveOnlineKeepMounted(): void {
    if (!this.resolveShow) return;
    const cfg: JoinConfig = {
      name: accountState.get()?.name ?? randomName(),
      hatId: this.selectedHat,
      color: this.selectedColor,
      action: 'online'
    };
    this.enterOnlineMode();
    const resolve = this.resolveShow;
    this.resolveShow = null;
    resolve(cfg);
  }

  /** Programmatic equivalent of clicking the ONLINE menu item — used by
   *  the join-link path in main.ts so a user landing on `?join=...` flows
   *  straight into the OnlineFlowController without first having to pick
   *  ONLINE manually. Idempotent: no-op if show() hasn't been awaited yet. */
  triggerOnlineFromJoinIntent(): void {
    this.resolveOnlineKeepMounted();
  }

  /** Slide the menu out and mark the home as online-active. Idempotent —
   *  re-entering doesn't restart the slide. Disposes the menu nav so its
   *  arrow-key listeners don't fight the OnlineFlowController's nav. */
  enterOnlineMode(): void {
    if (this.root.classList.contains('online-active')) return;
    this.root.classList.add('online-active');
    this.activeNav?.dispose();
    this.activeNav = null;
  }

  /** Slide the menu back in, restore main-menu nav focused on ONLINE
   *  MATCH (the row the user came from). Called by the online flow when
   *  the user confirms "Leave online play". The home's show() promise
   *  must already have been re-armed by the caller before this method
   *  runs (so the user's next pick can resolve). */
  exitOnlineMode(): void {
    if (!this.root.classList.contains('online-active')) return;
    this.root.classList.remove('online-active');
    this.root.classList.remove('party-active');
    // Rebuild + remount the main-menu nav focused on ONLINE MATCH.
    this.mainMenuNav = this.buildMainMenuNav('online');
    this.activeNav = this.mainMenuNav;
    if (this.navActivated) this.mainMenuNav.mount();
  }

  /** Toggle the wide-canvas party mode on or off. count == 1 = solo (520
   *  wide); count >= 2 = party (1300 wide). Drives a CSS transition + a
   *  three.js renderer.setSize call so the camera aspect updates with
   *  the new canvas dimensions. */
  setPartyMode(count: number): void {
    const isParty = count >= 2;
    const wasParty = this.root.classList.contains('party-active');
    if (isParty === wasParty) return;
    if (isParty) this.root.classList.add('party-active');
    else this.root.classList.remove('party-active');
    // The CSS transition takes 360ms; we schedule a few renderer.setSize
    // updates across that window so the camera aspect tracks the canvas
    // width without dropping pixels at intermediate sizes.
    this.scheduleCanvasResize();
  }

  /** Hand to MultiCharacterStage / OnlineFlowController so they can graft
   *  additional characters into the existing scene + project head badges
   *  over the canvas without us having to expose every internal field. */
  getStageHandle(): {
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    badgeRoot: HTMLElement;
    getCanvasSize(): { width: number; height: number };
    basePositions: readonly [number, number, number][];
  } {
    const previewEl = this.root.querySelector('.preview') as HTMLElement;
    return {
      scene: this.scene,
      camera: this.camera,
      badgeRoot: previewEl,
      getCanvasSize: () => {
        const rect = this.canvas.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
      },
      basePositions: this.basePositions
    };
  }

  private scheduleCanvasResize(): void {
    const start = performance.now();
    const tickResize = (): void => {
      if (this.disposed) return;
      const rect = this.canvas.getBoundingClientRect();
      const cw = Math.max(1, Math.round(rect.width));
      const ch = Math.max(1, Math.round(rect.height));
      // Update three.js renderer + camera each frame the transition is
      // active. We don't read CSS px-resolution; setSize already accepts
      // logical px and respects setPixelRatio internally.
      this.renderer.setSize(cw, ch, false);
      this.camera.aspect = cw / ch;
      this.camera.updateProjectionMatrix();
      if (performance.now() - start < 400) requestAnimationFrame(tickResize);
    };
    requestAnimationFrame(tickResize);
  }

  // -------------------------------------------------------------------------
  // DOM

  /** Update every translatable label in the home DOM in place. Called
   *  from the i18n locale-change subscription so the live UI behind the
   *  settings modal flips with the rest. Touches text nodes only — the
   *  3D preview canvas, character pose, and any focused input value are
   *  preserved. */
  private refreshLocale(): void {
    if (!this.root) return;

    // Main menu items.
    for (const m of MENU_ITEMS) {
      const btn = this.root.querySelector<HTMLButtonElement>(`.menu-item[data-menu="${m.id}"]`);
      if (btn) btn.textContent = t(m.labelKey);
    }

    // "YOU" head-label badge — the SVG carries the localized text inside.
    const headLabel = this.root.querySelector('.head-label') as HTMLElement | null;
    if (headLabel) {
      headLabel.innerHTML = renderHeadLabelSvg({
        text: t('home.you'),
        ...badgeColorsFromBase(this.selectedColor)
      });
    }

    // Form-panel field labels (Hat / Color).
    const fieldLabels = Array.from(this.root.querySelectorAll<HTMLElement>('.form-panel .field-label'));
    const labelKeys = ['home.hat', 'home.color'];
    fieldLabels.forEach((el, i) => {
      if (labelKeys[i]) el.textContent = t(labelKeys[i]!);
    });

    // 'No Hat' label inside the first hat option.
    const noHatLabel = this.root.querySelector<HTMLElement>('[data-hat="none"] .label');
    if (noHatLabel) noHatLabel.textContent = t('hats.none');

    // 'Custom' colour swatch label.
    const customTxt = this.root.querySelector<HTMLElement>('.color-opt.custom .txt');
    if (customTxt) customTxt.textContent = t('home.custom');

    // DONE button on the changing-room form.
    const doneBtn = this.root.querySelector<HTMLButtonElement>('.done-btn');
    if (doneBtn) doneBtn.textContent = t('home.done');

    // Stats title pill ("Brawling Noob").
    const titleEl = this.root.querySelector<HTMLElement>('.lvl-card .title');
    if (titleEl) titleEl.textContent = t(computeHomeStats().titleKey);
  }

  /** Refresh the bottom-left stats display from the latest account state.
   *  Called when the account is mutated (rename, level-up animation finishes). */
  refreshStats(): void {
    if (!this.root) return;
    const stats = computeHomeStats();
    const lvlEl = this.root.querySelector<HTMLElement>('.lvl-card .lvl');
    if (lvlEl) lvlEl.textContent = `LVL ${stats.level}`;
    const xpTextEl = this.root.querySelector<HTMLElement>('.lvl-card .xp-text');
    if (xpTextEl) xpTextEl.textContent = stats.maxLevel ? 'MAX' : `${stats.xpInto}/${stats.xpInto + stats.xpToNext} XP`;
    const xpFill = this.root.querySelector<HTMLElement>('.lvl-card .xp-fill');
    if (xpFill) {
      const pct = stats.maxLevel
        ? 100
        : Math.max(0, Math.min(100, (stats.xpInto / Math.max(1, stats.xpInto + stats.xpToNext)) * 100));
      xpFill.style.width = `${pct}%`;
    }
    const winsEl = this.root.querySelector<HTMLElement>('.pill.wins span');
    if (winsEl) winsEl.textContent = `${stats.totalWins} Wins`;
  }

  /** Triggered after a true return-to-home transition (not Play Again).
   *  Compares the live account level against the last seen level; on a
   *  bump, plays the level-up SFX and pulses the LVL card, then commits the
   *  new level to localStorage. Network failures are silent — the next
   *  return-to-home will retry. */
  async runLevelUpCheckOnEnter(): Promise<void> {
    if (this.disposed || !this.root) return;
    let account: AccountDTO | null = accountState.get();
    try {
      account = await accountState.refresh();
    } catch {
      // refresh() already swallows errors; keep whatever we had.
    }
    if (!account) return;
    this.refreshStats();
    const last = getLastSeenLevel();
    if (last === 0) {
      // First boot with this token — record current level without celebrating.
      setLastSeenLevel(account.level);
      return;
    }
    if (account.level > last) {
      this.playLevelUpFx();
      setLastSeenLevel(account.level);
    } else if (account.level < last) {
      // Server level dropped (rotate? db wipe?) — re-baseline silently.
      setLastSeenLevel(account.level);
    }
  }

  private playLevelUpFx(): void {
    try {
      menuAudio.playLevelUp?.();
    } catch {}
    const card = this.root.querySelector<HTMLElement>('.lvl-card');
    if (!card) return;
    card.classList.remove('level-up-pulse');
    // Force a reflow so the animation re-triggers if it was already applied.
    void card.offsetWidth;
    card.classList.add('level-up-pulse');
    setTimeout(() => card.classList.remove('level-up-pulse'), 1700);
  }

  private renderDom(): string {
    const menuButtons = MENU_ITEMS.map((m) => `
      <button class="menu-item" data-menu="${m.id}">${t(m.labelKey)}</button>
    `).join('');

    const hatButtons = HATS.map((h) => `
      <button class="hat-opt${h.id === this.selectedHat ? ' selected' : ''}" data-hat="${h.id}">
        <div class="inner" data-hat-inner="${h.id}">
          ${h.id === 'none' ? `<div class="label">${t('hats.none')}</div>` : ''}
        </div>
        <img class="check" src="/ui/large-check-circle.svg" alt="" />
      </button>
    `).join('');

    // If the stored/current colour isn't one of the presets, the custom
    // slot lights up instead and carries the picked hex as both its dot
    // colour and the color-input's initial value.
    const isCustomColor = !PRESET_COLORS.includes(this.selectedColor);
    const colorButtons = PRESET_COLORS.map((c) => `
      <button class="color-opt${c === this.selectedColor ? ' selected' : ''}" data-color="${c}">
        <div class="swatch"><div class="dot" style="background:${c}"></div></div>
        <img class="check" src="/ui/small-check-circle.svg" alt="" />
      </button>
    `).join('');
    const customDotBg = isCustomColor ? this.selectedColor : '#D8D8D8';
    const customInitial = isCustomColor ? this.selectedColor : '#E42A1E';

    const stats = computeHomeStats();
    const xpPct = stats.maxLevel
      ? 100
      : Math.max(0, Math.min(100, (stats.xpInto / Math.max(1, stats.xpInto + stats.xpToNext)) * 100));
    const xpText = stats.maxLevel ? 'MAX' : `${stats.xpInto}/${stats.xpInto + stats.xpToNext} XP`;

    return `
      <div class="floor-fill"></div>
      <div class="stage">
        <div class="wide-stage">

          <div class="preview"><canvas></canvas></div>

          <div class="head-label">${renderHeadLabelSvg({ text: t('home.you'), ...badgeColorsFromBase(this.selectedColor) })}</div>

          <nav class="menu-list">${menuButtons}</nav>


          <div class="form-panel">
            <div>
              <div class="field-label">${t('home.hat')}</div>
              <div class="hat-row">${hatButtons}</div>
            </div>
            <div>
              <div class="field-label">${t('home.color')}</div>
              <div class="color-row">
                ${colorButtons}
                <button class="color-opt custom${isCustomColor ? ' selected' : ''}" data-color-custom>
                  <div class="swatch"><div class="dot" style="background:${customDotBg}"></div><div class="txt">${t('home.custom')}</div></div>
                  <img class="check" src="/ui/small-check-circle.svg" alt="" />
                  <input type="color" value="${customInitial}" />
                </button>
              </div>
            </div>
            <div class="done-row">
              <button class="done-btn">${t('home.done')}</button>
            </div>
          </div>

        </div>
      </div>

      <div class="viewport-hud">
        <div class="lvl-card">
          <div class="lvl">LVL ${stats.level}</div>
          <div class="title">${t(stats.titleKey)}</div>
          <div class="xp-text">${xpText}</div>
          <div class="xp-track"><div class="xp-fill" style="width:${xpPct}%"></div></div>
        </div>
        <div class="bottom-pills">
          <div class="pill wins"><img src="/ui/wins-icon.webp" alt="" /><span>${stats.totalWins} Wins</span></div>
        </div>
      </div>

      <button class="dev-toggle">Dev Mode</button>
      <div class="dev-panel">
        <h3>Hat Config <button class="dev-copy">Copy</button></h3>
        <div class="dev-row"><label>Scale</label><input type="range" min="0.1" max="3" step="0.01" data-dev="scale" /><span class="val" data-devval="scale"></span></div>
        <div class="dev-row"><label>X</label><input type="range" min="-0.8" max="0.8" step="0.005" data-dev="offsetX" /><span class="val" data-devval="offsetX"></span></div>
        <div class="dev-row"><label>Y (Height)</label><input type="range" min="-0.5" max="1.5" step="0.005" data-dev="offsetY" /><span class="val" data-devval="offsetY"></span></div>
        <div class="dev-row"><label>Z</label><input type="range" min="-0.8" max="0.8" step="0.005" data-dev="offsetZ" /><span class="val" data-devval="offsetZ"></span></div>
        <div class="dev-row"><label>Yaw°</label><input type="range" min="-180" max="180" step="1" data-dev="yawDeg" /><span class="val" data-devval="yawDeg"></span></div>
        <div class="dev-row"><label>Pitch°</label><input type="range" min="-90" max="90" step="1" data-dev="pitchDeg" /><span class="val" data-devval="pitchDeg"></span></div>
        <div class="dev-row"><label>Roll°</label><input type="range" min="-90" max="90" step="1" data-dev="rollDeg" /><span class="val" data-devval="rollDeg"></span></div>
        <div class="dev-upload">
          <label>Upload GLB (preview only)</label>
          <input type="file" accept=".glb,model/gltf-binary" />
        </div>
        <div class="dev-readout"></div>
      </div>
    `;
  }

  private wireInputs(): void {
    this.doneBtn = this.root.querySelector('.done-btn') as HTMLButtonElement;

    // Click listeners on the changing-room buttons (hats, colors, done,
    // custom-color picker swatch) are NOT attached here — they're owned by
    // the changing-room MenuNav so mouse + keyboard + gamepad all route
    // through the same activate codepath (which also plays the UI click).
    // The native <input type="color"> still needs its own 'input' listener
    // because the color picker's value-change event isn't a "click" the nav
    // can synthesize.
    const customBtn = this.root.querySelector('.color-opt.custom') as HTMLButtonElement;
    const customInput = customBtn.querySelector('input[type="color"]') as HTMLInputElement;
    const customDot = customBtn.querySelector('.dot') as HTMLElement;
    customInput.addEventListener('input', () => {
      const c = customInput.value;
      customDot.style.background = c;
      this.selectColor(c, customBtn);
    });
  }

  /** Callback used by the hat MenuNav items. Extracted so the per-button
   *  click wiring and the nav activate path share one implementation. */
  private selectHat(id: HatSelection): void {
    this.selectedHat = id;
    for (const b of Array.from(this.root.querySelectorAll('.hat-opt'))) {
      b.classList.toggle('selected', (b as HTMLElement).dataset.hat === id);
    }
    this.applyHat(id);
    this.persistProfile();
    if (this.devMode) this.syncDevFromConfig();
  }

  private persistProfile(): void {
    saveProfile({
      hatId: this.selectedHat,
      color: this.selectedColor
    });
  }

  /** Build the 5-item vertical main-menu nav. Arrow up/down (or gamepad
   *  dpad / left stick) walks the list; Enter / A activates. Mouse hover
   *  syncs the focus marker into the nav so mouse and controller users see
   *  the same highlighted pill at all times. Construction only — caller is
   *  responsible for mount()/dispose() via activateNav or swapNav. The
   *  optional `initialMenuId` preselects a specific row — used when
   *  returning from the changing room, which should land focus back on
   *  CHANGING ROOM rather than defaulting to the top. */
  private buildMainMenuNav(initialMenuId?: MenuItemId): MenuNav {
    const items: MenuItem[] = MENU_ITEMS.map((m, i) => {
      const el = this.root.querySelector(`.menu-item[data-menu="${m.id}"]`) as HTMLElement;
      return {
        el,
        row: i,
        col: 0,
        onActivate: () => this.handleMenuClick(m.id)
      };
    });
    const initialIndex = initialMenuId
      ? Math.max(0, MENU_ITEMS.findIndex((m) => m.id === initialMenuId))
      : 0;
    return new MenuNav({ items, initialIndex });
  }

  /** Turn on menu input. Required for the first-time flow where the home
   *  mounts inside the splash — we delay this until splash has dismissed,
   *  so Arrow/Enter during the splash hit splash's own key handler rather
   *  than navigating the (invisible) menu. Standalone returns call this
   *  immediately after construction. Idempotent. */
  activateNav(): void {
    if (this.navActivated) return;
    this.navActivated = true;
    this.activeNav?.mount();
  }

  /** Tear down whatever nav is currently active and install a new one.
   *  The new nav only `mount()`s if the home is already interactive — see
   *  activateNav() — so a swap-during-splash (we don't do this today, but
   *  defensively) stays inert until the user sees the home. */
  private swapNav(next: MenuNav): void {
    this.activeNav?.dispose();
    this.activeNav = next;
    if (this.navActivated) next.mount();
  }

  private handleMenuClick(id: MenuItemId): void {
    if (id === 'changing') {
      this.enterChangingRoom();
      return;
    }
    if (id === 'mapEditor') {
      this.resolveAndDismiss('editor');
      return;
    }
    if (id === 'online') {
      // Online play stays on the home screen — slide the menu out and
      // resolve show() with action='online', without disposing. The
      // OnlineFlowController will mount its panel on top of the still-
      // live home and use getStageHandle() to render party-mates next
      // to the local character.
      this.resolveOnlineKeepMounted();
      return;
    }
    if (id === 'leaderboard') {
      this.resolveAndDismiss('leaderboard');
      return;
    }
    if (id === 'settings') {
      this.openSettings();
      return;
    }
    this.resolveAndDismiss('play');
  }

  /** Open the settings modal. Disposes the home main-menu nav so its
   *  keyboard listeners don't fight the modal's own MenuNav; re-mounts
   *  the main-menu nav on dismissal so the home is interactive again. */
  private openSettings(): void {
    this.activeNav?.dispose();
    showSettingsModal({
      onClose: () => {
        if (this.disposed) return;
        if (this.navActivated) this.activeNav?.mount();
      }
    });
  }

  private enterChangingRoom(): void {
    if (this.inChangingRoom) return;
    this.inChangingRoom = true;
    this.root.classList.add('changing');
    menuAudio.playChangingRoomSweep();
    this.swapNav(this.buildChangingRoomNav());
  }

  private leaveChangingRoom(): void {
    if (!this.inChangingRoom) return;
    this.inChangingRoom = false;
    this.root.classList.remove('changing');
    menuAudio.playChangingRoomSweep();
    // Drop the changing-room nav immediately so held Enter / A presses from
    // the DONE activation can't retrigger anything during the pan.
    this.activeNav?.dispose();
    this.activeNav = null;
    // Rebuild the main-menu nav focused on CHANGING ROOM — that's the row
    // the user came from, so returning to it keeps their mental model
    // intact rather than jumping back to the top of the menu.
    this.mainMenuNav = this.buildMainMenuNav('changing');
    // Defer mounting until the pan animation finishes, so the menu is only
    // interactive once the main screen has visibly landed. Matches the
    // 560ms .wide-stage transition above — any held-down activate press
    // from DONE has usually released by then, and MenuGamepad's start-
    // time seeding catches the case where it hasn't.
    const PAN_DURATION_MS = 560;
    window.setTimeout(() => {
      if (this.disposed) return;
      // Late-leaving guard: if the user re-entered the changing room
      // during the pan for any reason, don't install the main-menu nav
      // over the now-correct changing-room nav.
      if (this.inChangingRoom) return;
      this.activeNav = this.mainMenuNav;
      if (this.navActivated) this.mainMenuNav!.mount();
    }, PAN_DURATION_MS);
  }

  /** 2D grid layout for changing-room navigation.
   *    rows 0–2 — hats (6 cols × 3 rows, last row has 5 items so col 5 is empty)
   *    row 3 — color swatches (0..5) + custom (col 6)
   *    row 4 — DONE (col 0) */
  private buildChangingRoomNav(): MenuNav {
    const items: MenuItem[] = [];

    // Hats — sequential fill across 6 columns × 3 rows. 17 items means the
    // last cell (row 2 / col 5) is empty; MenuNav filters items by row so
    // navigating into row 2 clamps to the populated cols (0..4).
    const hatButtons = Array.from(this.root.querySelectorAll('.hat-opt')) as HTMLButtonElement[];
    hatButtons.forEach((btn, i) => {
      const row = Math.floor(i / 6);
      const col = i % 6;
      const id = btn.dataset.hat as HatSelection;
      items.push({
        el: btn,
        row, col,
        onActivate: () => this.selectHat(id)
      });
    });

    // Colors — 6 presets on cols 0..5, custom on col 6.
    const colorButtons = Array.from(this.root.querySelectorAll('.color-opt[data-color]')) as HTMLButtonElement[];
    colorButtons.forEach((btn, i) => {
      items.push({
        el: btn,
        row: 3, col: i,
        onActivate: () => this.selectColor(btn.dataset.color!, btn)
      });
    });

    const customBtn = this.root.querySelector('.color-opt.custom') as HTMLButtonElement;
    const customInput = customBtn.querySelector('input[type="color"]') as HTMLInputElement;
    const customDot = customBtn.querySelector('.dot') as HTMLElement;
    items.push({
      el: customBtn,
      row: 3, col: 6,
      // Activating the custom swatch both (a) commits whatever hex the
      // input currently holds as the active colour and (b) opens the OS
      // picker so the user can pick a different one. The picker's own
      // 'input' listener (wireInputs) keeps the selection in sync as they
      // scrub. .click() is only synthesised for key/controller activations
      // because a mouse click on the button already hit the transparent
      // input overlay, which opened the picker natively — re-calling it
      // here would close the just-opened picker in Chrome.
      onActivate: (source) => {
        customDot.style.background = customInput.value;
        this.selectColor(customInput.value, customBtn);
        if (source === 'key') customInput.click();
      }
    });

    // Done.
    items.push({
      el: this.doneBtn,
      row: 4, col: 0,
      onActivate: () => this.leaveChangingRoom()
    });

    return new MenuNav({
      items,
      // Controller B button leaves the changing room — same end state as
      // pressing DONE.
      onCancel: () => this.leaveChangingRoom()
    });
  }


  private selectColor(c: string, btn: HTMLButtonElement): void {
    this.selectedColor = c;
    for (const b of Array.from(this.root.querySelectorAll('.color-opt'))) b.classList.remove('selected');
    btn.classList.add('selected');
    this.applyColor(c);
    this.persistProfile();
  }

  private async populateHatIcons(): Promise<void> {
    const modelHats = HATS.filter((h): h is HatDef & { id: HatId } => h.id !== 'none');
    // Serialised on purpose — each hat icon spins up its own WebGPURenderer
    // (see renderHatIcon) and the older Promise.all caused 17 concurrent
    // device allocations, which collided with the splash's renderer + the
    // main preview renderer and left one of their textures null at
    // material-build time ("texture( value ) expects a valid instance of
    // THREE.Texture" at first render). Sequencing keeps peak WebGPU
    // context count at 1 above the baseline.
    for (const h of modelHats) {
      if (this.disposed) return;
      try {
        const canvas = await renderHatIcon(h.id, 224);
        this.iconCanvases.push(canvas);
        const inner = this.root.querySelector(`[data-hat-inner="${h.id}"]`) as HTMLElement | null;
        if (inner && !this.disposed) {
          inner.innerHTML = '';
          inner.appendChild(canvas);
        }
      } catch (err) {
        console.warn(`[HomeScreen] hat icon render failed (${h.id}):`, err);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Preview scene. The character's ground shadow is a baked radial-gradient
  // texture on a plane rather than a real shadow-mapped projection — the
  // WebGPU TSL ShadowMaterial pipeline crashes with a null texture on some
  // drivers, so we dodge shadow maps entirely. Visual is identical (soft
  // diffuse pool centred under the character, drifting slightly forward).

  private async setupPreview(): Promise<void> {
    this.canvas = this.root.querySelector('.preview canvas') as HTMLCanvasElement;
    // Full stage-height canvas. FOV 28° + distance 8.4 gives ~4.2 units of
    // vertical visible world range — enough to frame the character plus
    // plenty of ground in front of the feet so the soft drop shadow can
    // drape toward the camera.
    const w = 520, h = 1080;

    this.scene = new THREE.Scene();
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));

    // Main aesthetic light — upper-left, warm, does not cast shadow.
    this.dirLight = new THREE.DirectionalLight(0xfff5d8, 2.4);
    this.dirLight.position.set(-3.5, 6, 3);
    this.scene.add(this.dirLight);

    this.camera = new THREE.PerspectiveCamera(28, w / h, 0.1, 50);
    this.camera.position.set(0, 1.5, 8.4);
    // Look slightly above pelvis so the character + hat sit lower in the
    // 1080-tall canvas, leaving a comfortable stretch of sky for the
    // larger YOU badge to hang close above the hat.
    this.camera.lookAt(0, 1.2, 0);

    this.view = new CharacterView(CharacterView.palettePlayer(), {
      hatId: this.selectedHat === 'none' ? null : this.selectedHat
    });
    this.scene.add(this.view.group);

    // Fake ground shadow: a plane rotated flat, textured with a canvas-
    // painted radial gradient that fades from opaque dark-teal to fully
    // transparent at the edges. Shifted slightly forward (+z) so the
    // visible bulk of the shadow drifts toward the camera, matching the
    // "light from above, shadow toward user" brief. No shadow maps
    // involved — plays nicely with WebGPU's TSL material system.
    const shadowTexCanvas = document.createElement('canvas');
    shadowTexCanvas.width = shadowTexCanvas.height = 256;
    const sctx = shadowTexCanvas.getContext('2d')!;
    // Tighter gradient + higher core opacity than before. Previous stops
    // peaked at 0.55 and spread to 0 by the edge of a 3.4m plane —
    // together the falloff was soft enough to read as "no shadow". Now
    // the core sits at 0.85 and the falloff concentrates in the inner
    // half of the plane, giving a dense core that fades cleanly.
    const grad = sctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    grad.addColorStop(0.00, 'rgba(11, 42, 56, 0.85)');
    grad.addColorStop(0.30, 'rgba(11, 42, 56, 0.55)');
    grad.addColorStop(0.60, 'rgba(11, 42, 56, 0.18)');
    grad.addColorStop(1.00, 'rgba(11, 42, 56, 0.00)');
    sctx.fillStyle = grad;
    sctx.fillRect(0, 0, 256, 256);
    const shadowTex = new THREE.CanvasTexture(shadowTexCanvas);
    shadowTex.colorSpace = THREE.SRGBColorSpace;
    shadowTex.anisotropy = 4;

    const shadowPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.5, 1.5),
      new THREE.MeshBasicMaterial({
        map: shadowTex,
        transparent: true,
        depthWrite: false,
        opacity: 1
      })
    );
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.position.set(0, 0.02, 0.3);
    this.scene.add(shadowPlane);

    this.renderer = new WebGPURenderer({ canvas: this.canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    await this.renderer.init();
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x000000, 0);

    const pmrem = new PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    // Precompile all materials in the scene so the first render frame
    // doesn't race against any async texture setup (notably the PMREM
    // environment map). Without this, running the home in parallel with
    // the splash's renderer occasionally left a texture node with a null
    // input at first draw → "texture( value ) expects a valid instance".
    await this.renderer.compileAsync(this.scene, this.camera);
  }

  private wireDragRotation(): void {
    const DRAG_RAD_PER_PX = 0.008;
    this.canvas.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.dragPointerId = e.pointerId;
      this.canvas.setPointerCapture(e.pointerId);
      this.canvas.classList.add('dragging');
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (!this.dragging || e.pointerId !== this.dragPointerId) return;
      this.userYaw += e.movementX * DRAG_RAD_PER_PX;
    });
    const endDrag = (e: PointerEvent): void => {
      if (e.pointerId !== this.dragPointerId) return;
      this.dragging = false;
      this.dragPointerId = -1;
      this.canvas.classList.remove('dragging');
    };
    this.canvas.addEventListener('pointerup', endDrag);
    this.canvas.addEventListener('pointercancel', endDrag);
  }

  // -------------------------------------------------------------------------
  // Dev mode

  private activeHatConfig(): HatConfig | null {
    return this.selectedHat === 'none' ? null : CONFIG.hats[this.selectedHat];
  }

  private wireDevMode(): void {
    const toggle = this.root.querySelector('.dev-toggle') as HTMLButtonElement;
    this.devReadout = this.root.querySelector('.dev-readout') as HTMLElement;
    // Hide the Dev Mode button entirely outside of dev sessions. The
    // dev-panel sliders are still wired up (cheap) but are unreachable
    // without the toggle, so the panel never opens for normal players.
    if (!isDevMode()) {
      toggle.style.display = 'none';
    }
    toggle.addEventListener('click', () => {
      this.devMode = !this.devMode;
      this.root.classList.toggle('dev-on', this.devMode);
      toggle.textContent = this.devMode ? 'Close Dev' : 'Dev Mode';
      if (this.devMode) this.syncDevFromConfig();
    });

    for (const key of DEV_KEYS) {
      const slider = this.root.querySelector(`input[data-dev="${key}"]`) as HTMLInputElement;
      slider.addEventListener('input', () => {
        const cfg = this.activeHatConfig();
        if (!cfg) return;
        (cfg as unknown as Record<string, number>)[key] = parseFloat(slider.value);
        this.updateDevReadout();
      });
    }

    (this.root.querySelector('.dev-copy') as HTMLButtonElement).addEventListener('click', () => {
      const txt = this.formatModifiedHatsJson();
      if (!txt) return;
      navigator.clipboard?.writeText(txt).catch(() => { /* ignore */ });
    });

    const fileInput = this.root.querySelector('.dev-upload input[type="file"]') as HTMLInputElement;
    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      try {
        const buf = await file.arrayBuffer();
        const loader = createGLTFLoader();
        loader.parse(buf, '', (gltf) => {
          const slot: HatId = this.selectedHat === 'none' ? 'top' : this.selectedHat;
          this.view.setHat({ group: gltf.scene, config: CONFIG.hats[slot] });
        }, (err) => {
          console.warn('[dev] GLB parse failed:', err);
          alert('GLB parse failed — check the file and the console for details.');
        });
      } catch (err) {
        console.warn('[dev] GLB read failed:', err);
      }
    });
  }

  private syncDevFromConfig(): void {
    const cfg = this.activeHatConfig();
    const sliders = this.root.querySelectorAll('input[data-dev]');
    sliders.forEach((el) => {
      const slider = el as HTMLInputElement;
      slider.disabled = cfg == null;
    });
    if (!cfg) {
      this.updateDevReadout();
      return;
    }
    const h = cfg as unknown as Record<string, number>;
    for (const key of DEV_KEYS) {
      const slider = this.root.querySelector(`input[data-dev="${key}"]`) as HTMLInputElement | null;
      if (slider) slider.value = String(h[key]);
    }
    this.updateDevReadout();
  }

  private updateDevReadout(): void {
    const cfg = this.activeHatConfig();
    if (!cfg) {
      for (const key of DEV_KEYS) {
        const span = this.root.querySelector(`.val[data-devval="${key}"]`);
        if (span) span.textContent = '—';
      }
    } else {
      const h = cfg as unknown as Record<string, number>;
      for (const key of DEV_KEYS) {
        const span = this.root.querySelector(`.val[data-devval="${key}"]`);
        if (span) span.textContent = formatNum(h[key]!);
      }
    }
    const json = this.formatModifiedHatsJson();
    this.devReadout.textContent = json || '(no hats modified yet)';
  }

  private formatModifiedHatsJson(): string {
    const modified: Record<string, HatConfig> = {};
    for (const id of Object.keys(CONFIG.hats) as HatId[]) {
      const current = CONFIG.hats[id];
      const snap = this.hatConfigSnapshot[id];
      let changed = false;
      for (const key of DEV_KEYS) {
        if ((current as unknown as Record<string, number>)[key] !== (snap as unknown as Record<string, number>)[key]) {
          changed = true;
          break;
        }
      }
      if (changed) modified[id] = current;
    }
    const ids = Object.keys(modified);
    if (ids.length === 0) return '';
    const lines = ids.map((id) => {
      const c = modified[id] as unknown as Record<string, number>;
      const parts = DEV_KEYS.map((k) => `"${k}": ${formatNum(c[k]!)}`).join(', ');
      return `  "${id}": { ${parts} }`;
    });
    return `{\n${lines.join(',\n')}\n}`;
  }

  /** Rewrite both snapshots from the cached spawn pose + current yaw state.
   *
   *  Head orientation:
   *    qHead = qYaw(world +Y, headLookYaw) · qPitch(world +X, headLookPitch + nod)
   *  The beat-locked nod angle is FOLDED INTO THE PITCH before composing
   *  with yaw. That works because both rotations are around the same
   *  axis (world +X in the rest frame, which is the head's ear-axis
   *  after yaw is applied on top), and rotations around a common axis
   *  commute and add. The effect is a nod along the head's current gaze
   *  line — if the head is already looking up-right at the cursor, the
   *  nod tips the face "forward along that gaze", not straight down in
   *  world space.
   */
  private applyPose(): void {
    const yawBody = this.userYaw;
    const sinB = Math.sin(yawBody), cosB = Math.cos(yawBody);
    const qBody = quatFromAxisAngle({ x: 0, y: 1, z: 0 }, yawBody);
    const qYawHead = quatFromAxisAngle({ x: 0, y: 1, z: 0 }, this.headLookYaw);
    const nodPitch = this.computeHeadNodPitch();
    const qPitchHead = quatFromAxisAngle({ x: 1, y: 0, z: 0 }, this.headLookPitch + nodPitch);
    const qHead = quatMul(qYawHead, qPitchHead);

    for (let i = 0; i < BONE_COUNT; i++) {
      const o = i * 7;
      const p = this.basePositions[i]!;
      const rx = cosB * p[0] + sinB * p[2];
      const rz = -sinB * p[0] + cosB * p[2];
      const q = i === Bone.Head ? qHead : qBody;
      this.currSnap.bones[o + 0] = rx;
      this.currSnap.bones[o + 1] = p[1];
      this.currSnap.bones[o + 2] = rz;
      this.currSnap.bones[o + 3] = q.x;
      this.currSnap.bones[o + 4] = q.y;
      this.currSnap.bones[o + 5] = q.z;
      this.currSnap.bones[o + 6] = q.w;
    }
    this.prevSnap.bones.set(this.currSnap.bones);
    this.currSnap.facingYaw = yawBody;
    this.currSnap.pelvisY = this.basePositions[Bone.Pelvis]![1];
    this.prevSnap.facingYaw = yawBody;
    this.prevSnap.pelvisY = this.currSnap.pelvisY;
  }

  /** Per-beat head nod, returned as an extra pitch angle (rad, positive
   *  = chin toward chest). Envelope is two half-cosines stitched at the
   *  bottom of the nod:
   *
   *    down stroke (0 .. NOD_DOWN_FRAC of the beat)
   *      env = 0.5 − 0.5·cos(π·t)   — rises smoothly from 0 to 1
   *    up stroke   (NOD_DOWN_FRAC .. 1)
   *      env = 0.5 + 0.5·cos(π·t)   — falls smoothly from 1 back to 0
   *
   *  Both halves are sine-eased, so the motion is C¹ everywhere: zero
   *  first-derivative at 0, at the bottom of the nod, and at the return
   *  to rest. The asymmetry the user asked for comes from the time
   *  split — the down stroke occupies only ~35% of the beat while the
   *  up stroke takes the remaining ~65%, so the same ease shape, when
   *  compressed into less time, reads as a "slightly sharper stop" at
   *  the bottom compared to the drawn-out glide back up. */
  private computeHeadNodPitch(): number {
    const bps = HomeScreen.NOD_BPM / 60;
    const t = performance.now() / 1000 - HomeScreen.NOD_START_DELAY_SEC;
    // (x % 1 + 1) % 1 keeps phase in [0, 1) even when NOD_START_DELAY_SEC
    // is negative or larger than one beat — JS's `%` returns a negative
    // remainder for negative operands, which would break the dF branch.
    const phase = ((t * bps) % 1 + 1) % 1;
    const dF = HomeScreen.NOD_DOWN_FRAC;
    let env: number;
    if (phase < dF) {
      const x = phase / dF;
      env = 0.5 - 0.5 * Math.cos(x * Math.PI);
    } else {
      const x = (phase - dF) / (1 - dF);
      env = 0.5 + 0.5 * Math.cos(x * Math.PI);
    }
    return HomeScreen.NOD_AMPLITUDE * env * this.nodScale;
  }

  /** Ease nodScale toward 0 while the mouse has moved within NOD_IDLE_MS,
   *  toward 1 once the player has been still for that long. The phase
   *  clock in computeHeadNodPitch still ticks off performance.now(),
   *  so when the nod fades back in it lands on whichever beat the music
   *  is currently on — we only gate amplitude, not timing. */
  private stepNodScale(nowMs: number, dt: number): void {
    const target = (nowMs - this.lastMouseMoveMs) >= HomeScreen.NOD_IDLE_MS ? 1 : 0;
    const k = Math.min(1, dt * HomeScreen.NOD_FADE_RATE);
    this.nodScale += (target - this.nodScale) * k;
  }

  private applyColor(hex: string): void {
    const palette = paletteFromBase(hex);
    this.view.setPalette(palette);
    this.updateBadgeColor(hex);
  }

  /** Re-render the "YOU" badge SVG with colors derived from the player's
   *  pick. badgeColorsFromBase caps lightness so near-white picks land on
   *  a mid-gray badge instead of washing out against the eggshell sky. */
  private updateBadgeColor(hex: string): void {
    const label = this.root.querySelector('.head-label') as HTMLElement | null;
    if (!label) return;
    label.innerHTML = renderHeadLabelSvg({ text: t('home.you'), ...badgeColorsFromBase(hex) });
  }

  private applyHat(id: HatSelection): void {
    this.view.setHat(id === 'none' ? null : id);
  }

  // -------------------------------------------------------------------------
  // Head mouse-tracking

  /** Compute target yaw/pitch from the pointer relative to the head's
   *  on-screen centre, then exponentially ease the current angles toward
   *  the target so small mouse movements produce a smooth pan rather than
   *  snapping. Uses atan2 against a pseudo "focal distance" (viewport
   *  width) so yaw reaches a natural-feeling ~±42° at the screen edges
   *  rather than a linear ramp that clips weirdly off-center.
   *
   *  Head screen-centre is the horizontal centre of the preview canvas
   *  at ~14% down from its top — matching where the camera frames the head
   *  mesh (see setupPreview: lookAt y=1.2, FOV 28°, head renders around
   *  canvas y ≈ 153 per HomeScreen.ts:170).
   */
  private stepMouseHead(dt: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const hx = rect.left + rect.width * 0.5;
    const hy = rect.top + rect.height * 0.142;
    const dx = this.mouseX - hx;
    const dy = this.mouseY - hy;

    // Pseudo focal length in pixels. innerWidth*0.55 puts the screen edge at
    // about atan2(halfW, 0.55W) ≈ 42° of yaw — enough to read as "looking
    // off to one side" without over-rotating into a neck crank.
    const focal = Math.max(1, innerWidth * 0.55);
    const YAW_MAX = 0.95;   // ~54°
    const PITCH_MAX = 0.55; // ~31°
    const yawT = Math.max(-YAW_MAX, Math.min(YAW_MAX, Math.atan2(dx, focal)));
    const pitchT = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, Math.atan2(dy, focal)));

    // Exp ease with a time constant ~1/10s, clamped to keep fast frames
    // from over-stepping the target on small dt.
    const k = Math.min(1, dt * 10);
    this.headLookYaw += (yawT - this.headLookYaw) * k;
    this.headLookPitch += (pitchT - this.headLookPitch) * k;
  }

  private pollGamepad(dt: number): void {
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      const rx = pad.axes[2] ?? 0;
      const dead = 0.15;
      if (Math.abs(rx) < dead) continue;
      const norm = (rx - Math.sign(rx) * dead) / (1 - dead);
      this.userYaw += norm * 2.8 * dt;
      return;
    }
  }

  private loop = (): void => {
    if (this.disposed) return;
    this.rafHandle = requestAnimationFrame(this.loop);
    const now = performance.now();
    const dt = this.lastFrameMs === 0 ? 1 / 60 : Math.min(0.1, (now - this.lastFrameMs) / 1000);
    this.lastFrameMs = now;

    this.stepMouseHead(dt);
    this.pollGamepad(dt);
    this.stepNodScale(now, dt);
    this.applyPose();

    this.view.update(this.prevSnap, this.currSnap, 1, dt);

    // External per-frame work (party-mate characters, badge projection
    // updates). Wrapped in try-catch so a bad callback doesn't kill the
    // home's render loop.
    for (const cb of this.frameCallbacks) {
      try { cb(now, dt); } catch (err) { console.warn('[HomeScreen] frame callback threw', err); }
    }

    const ld = this.dirLight.position.clone().normalize().multiplyScalar(-1);
    this.view.material.setLightDir(ld);

    this.renderer.render(this.scene, this.camera);
  };

  /** Register a per-frame callback. Returns a detach function. */
  addFrameCallback(cb: (nowMs: number, dt: number) => void): () => void {
    this.frameCallbacks.push(cb);
    return () => {
      const i = this.frameCallbacks.indexOf(cb);
      if (i >= 0) this.frameCallbacks.splice(i, 1);
    };
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.scaler?.stop();
    this.scaler = null;
    this.detachLocaleListener?.();
    this.detachLocaleListener = null;
    removeEventListener('mousemove', this.onMouseMove);
    removeEventListener('pointermove', this.onMouseMove);
    this.activeNav?.dispose();
    this.activeNav = null;

    for (const c of this.iconCanvases) {
      const owner = (c as unknown as { _owner?: { renderer: WebGPURenderer } })._owner;
      if (owner) owner.renderer.dispose();
    }
    this.renderer.dispose();
    this.root.remove();
    this.styleEl.remove();
  }
}

function formatNum(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  return parseFloat(n.toFixed(3)).toString();
}
